import { useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment/moment";
import { IconUserSearch } from "@tabler/icons-react";
// @ts-expect-error pure js
import useTenants from "../../hooks/use-tenants";
// @ts-expect-error pure js
import { usePublicProperties } from "../../hooks/use-properties";
// @ts-expect-error pure js
import { TenantStatus } from "../../models/tenant_old.js";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
// @ts-expect-error pure js
import IsLoading from "../UI/IsLoading";
import { useToast } from "../UI/Toast/useToast";
import { Table } from "../UI/Table";
import Button from "../UI/Button";
import Status from "../UI/Status";
import Text from "../UI/Text";
import EmptyState from "../UI/EmptyState";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
import type { Tenant } from "../../models/tenant";
import type { Property } from "../../models/property";
import styles from "./LocataireCandidaturesList.module.css";

interface WithdrawCandidatureDialogProps {
  candidature: Tenant | null;
  propertyName?: string;
  onClose: () => void;
  onConfirm: (candidature: Tenant) => Promise<void>;
}

function WithdrawCandidatureDialog({
  candidature,
  propertyName,
  onClose,
  onConfirm,
}: WithdrawCandidatureDialogProps) {
  const [isWithdrawing, setIsWithdrawing] = useState(false);

  async function confirmHandler() {
    if (!candidature) return;
    setIsWithdrawing(true);
    try {
      await onConfirm(candidature);
    } finally {
      setIsWithdrawing(false);
    }
  }

  return (
    <ModalDialog
      open={candidature !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      header="Annuler ma candidature"
      size="sm"
    >
      <ModalDialogBody className={styles.dialogBody}>
        <Text>
          Votre candidature
          {propertyName ? ` pour le bien « ${propertyName} »` : ""} sera retirée
          et le bailleur n'y aura plus accès.
        </Text>
        <Text>
          Vous pourrez candidater à nouveau à ce bien plus tard si vous le
          souhaitez.
        </Text>
      </ModalDialogBody>
      <ModalDialogFooter>
        <Button variant="secondary" onClick={onClose} disabled={isWithdrawing}>
          Conserver ma candidature
        </Button>
        <Button
          variant="danger"
          onClick={confirmHandler}
          isLoading={isWithdrawing}
        >
          Annuler ma candidature
        </Button>
      </ModalDialogFooter>
    </ModalDialog>
  );
}

export default function LocataireCandidaturesList() {
  const toast = useToast();
  const { tenants, isLoading, withdrawCandidature } = useTenants({
    isLocataire: true,
  }) as {
    tenants: Tenant[];
    isLoading: boolean;
    withdrawCandidature: (tenantId: number) => Promise<void>;
  };
  const { publicPropertiesById } = usePublicProperties({ withById: true }) as {
    publicPropertiesById: Record<number, Property | undefined>;
  };
  const [candidatureToWithdraw, setCandidatureToWithdraw] =
    useState<Tenant | null>(null);

  const candidatures = tenants.filter((tenant) =>
    [
      TenantStatus.Candidat.status,
      TenantStatus.CandidatArchive.status,
    ].includes(tenant.status),
  );

  async function withdrawHandler(candidature: Tenant) {
    try {
      await withdrawCandidature(candidature.id);
      setCandidatureToWithdraw(null);
      toast.success({ title: "Candidature annulée" });
    } catch (error) {
      toast.error({
        title: "Impossible d'annuler la candidature",
        description: handleAPIError(error),
      });
    }
  }

  if (isLoading) return <IsLoading />;

  return (
    <div className={styles.container}>
      <WithdrawCandidatureDialog
        candidature={candidatureToWithdraw}
        propertyName={
          candidatureToWithdraw
            ? publicPropertiesById[candidatureToWithdraw.id_bien]?.nom
            : undefined
        }
        onClose={() => setCandidatureToWithdraw(null)}
        onConfirm={withdrawHandler}
      />
      {candidatures.length === 0 ? (
        <EmptyState
          icon={IconUserSearch}
          title="Aucune candidature pour le moment"
          description="Les candidatures que vous envoyez à un bailleur apparaîtront ici."
        />
      ) : (
        <Table variant="card">
          <Table.Header>
            <Table.Column>Date de candidature</Table.Column>
            <Table.Column>Bien</Table.Column>
            <Table.Column>Statut</Table.Column>
            <Table.Column align="right" aria-label="Actions" />
          </Table.Header>
          <Table.Body>
            {candidatures.map((candidature) => {
              const property = publicPropertiesById[candidature.id_bien];
              const isPending =
                candidature.status === TenantStatus.Candidat.status;
              return (
                <Table.Row key={candidature.id}>
                  <Table.Cell>
                    <Text as="div">
                      {moment(candidature.creation_date).format("DD MMMM YYYY")}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    {property ? (
                      <Link to={`/candidat?bien=${property.id}`}>
                        <Text as="span">{property.nom}</Text>
                      </Link>
                    ) : (
                      <Text as="div" color="muted">
                        Bien indisponible
                      </Text>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {isPending ? (
                      <Status
                        color="warning"
                        text="En cours d'étude par le bailleur"
                      />
                    ) : (
                      <Status color="danger" text="Refusée par le bailleur" />
                    )}
                  </Table.Cell>
                  <Table.Cell align="right">
                    {isPending && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setCandidatureToWithdraw(candidature)}
                      >
                        Annuler ma candidature
                      </Button>
                    )}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}
    </div>
  );
}
